/* ImportAction.js */

"use strict";

const _ = require("lodash");
const ImportScreenComponent = require("./ImportScreenComponent");
const ImportSummaryComponent = require("./ImportSummary/ImportSummaryComponent");
const {
    convertExpressionTags,
} = require("../../../../editors/auditpolicyeditor/utils/ruleLabelMapper");

class ImportAction {
    constructor(
        logger,
        gameEditor,
        modalUserFeedback,
        ConfigAuditPolicy,
        userSession,
        activityMonitor,
        steveIo,
        activeView,
        CustomFields,
        CommandSetEntityDef,
        TagsEntityDef
    ) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._configAuditPolicy = ConfigAuditPolicy;
        this._modalUserFeedback = modalUserFeedback;
        this._commandSetEntityDef = CommandSetEntityDef;
        this._tagsEntityDef = TagsEntityDef;
        this._steveIo = steveIo;
        this._activeView = activeView;
        this._activityMonitor = activityMonitor;
        this._activeOrgId = this._userSession.activeOrganization.id;
        this._conceptItemLabelValues = {};
        this._tagNameToIdMap = {};
        this.policyName = "";
        this._CustomFields = CustomFields;
        this.run = this.run.bind(this);
    }

    _isUuid(str) {
        const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
        return regex.exec(str) !== null;
    }

    _isRegExp(str) {
        try {
            new RegExp(str);
            return true;
        } catch (err) {
            return false;
        }
    }

    validateRules(rules) {
        let validationErrors = [];
        if (!_.isArray(rules)) {
            validationErrors.push("rules must be an array");
            return;
        }
        let names = {};
        for (let rule of rules) {
            if (!_.isString(rule.name) || rule.name.length === 0) {
                validationErrors.push({rule, type: "empty-name", msg: "name is required"});
            } else if (names[rule.name]) {
                validationErrors.push({rule, msg: "name must be unique"});
            }
            if (rule.description && !_.isString(rule.description)) {
                validationErrors.push({rule, msg: "description must be a string"});
            }
            if (rule.os && !_.isString(rule.os)) {
                validationErrors.push({rule, msg: "os must be a string"});
            }
            if (rule.conceptItemId && !this._isUuid(rule.conceptItemId)) {
                validationErrors.push({rule, msg: "conceptItemId must be a UUID"});
            }
            if (rule.scope && !this._isRegExp(rule.scope)) {
                validationErrors.push({rule, msg: "invalid scope regex"});
            }

            if (!rule.type) {
                validationErrors.push({rule, msg: "invalid type"});
            }

            if (!rule.thenExpression || !_.isObject(rule.thenExpression)) {
                validationErrors.push({rule, msg: "then expression is required"});
            }

            if (rule.type === "IF_THEN" && (!rule.ifExpression || !_.isObject(rule.ifExpression))) {
                validationErrors.push({rule, msg: "if expression is required"});
            }

            if (rule.type === "IF_THEN_ELSE") {
                if (!rule.ifExpression || !_.isObject(rule.ifExpression)) {
                    validationErrors.push({rule, msg: "if expression is required"});
                }

                if (!rule.elseExpression || !_.isObject(rule.elseExpression)) {
                    validationErrors.push({rule, msg: "else expression is required"});
                }
            }

            names[rule.name] = true;
        }
        return validationErrors;
    }

    generateErrorMsg(reason) {
        if (reason.errorCode !== "InvalidAudityPolicy") {
            return;
        }
        let msg = [];
        reason.validationErrors.forEach((err) => {
            if (typeof err === "string") {
                msg.push(err + "\r\n");
            } else {
                msg.push(`Rule: ${err.ruleName}, Error: ${err.msg} \r\n`);
            }
        });
        return msg;
    }

    processRule(policy, rule, ruleIdx, type) {
        if (!type || !rule) {
            return;
        }
        let typeFailed = false;
        switch (type) {
            case "conceptItem":
                if (rule.conceptItem) {
                    if (!rule.conceptItemId) {
                        policy.rules[ruleIdx].conceptItemId = _.get(
                            this._conceptItemLabelValues,
                            `${rule.conceptItem}.value`,
                            null
                        );
                        if (!policy.rules[ruleIdx].conceptItemId) {
                            typeFailed = true;
                        }
                    } else {
                        /* jshint ignore:start*/
                        if (
                            rule.conceptItemId !==
                            _.get(this._conceptItemLabelValues, `${rule.conceptItem}.value`, null)
                        ) {
                            //eslint-disable-line
                            /* jshint ignore:end*/
                            typeFailed = true;
                        }
                    }
                }
                break;
            case "cmdResultConceptItem":
                if (rule.commandSetResult.conceptItem) {
                    if (!rule.commandSetResult.conceptItemId) {
                        policy.rules[ruleIdx].commandSetResult.conceptItemId = _.get(
                            this._conceptItemLabelValues,
                            `${rule.commandSetResult.conceptItem}.value`,
                            null
                        );
                        if (!policy.rules[ruleIdx].commandSetResult.conceptItemId) {
                            typeFailed = true;
                        }
                    } else {
                        /* jshint ignore:start*/
                        if (
                            rule.commandSetResult.conceptItemId !==
                            _.get(
                                this._conceptItemLabelValues,
                                `${rule.commandSetResult.conceptItem}.value`,
                                null
                            )
                        ) {
                            //eslint-disable-line
                            /* jshint ignore:end*/
                            typeFailed = true;
                        }
                    }
                }
                break;
            case "rsiId":
                if (rule.restSyncItem) {
                    policy.rules[ruleIdx].rsiId = this._rsiIdsMap[rule.restSyncItem];
                    if (!policy.rules[ruleIdx].rsiId) {
                        typeFailed = true;
                    }
                }
                break;
            case "cmdResultRsiId":
                if (rule.commandSetResult.restSyncItem) {
                    policy.rules[ruleIdx].commandSetResult.restSyncItemId =
                        this._rsiIdsMap[rule.commandSetResult.restSyncItem];
                    if (!policy.rules[ruleIdx].commandSetResult.restSyncItemId) {
                        typeFailed = true;
                    }
                }
                break;
            case "cmdSet":
                if (rule.commandSetName) {
                    policy.rules[ruleIdx].commandSet = this._cmdSetValLabelMap[rule.commandSetName];
                    if (!policy.rules[ruleIdx].commandSet) {
                        typeFailed = true;
                    }
                }
                break;
        }
        return typeFailed;
    }

    run() {
        let importScreenComponent = new ImportScreenComponent(
            this._logger,
            this._userSession,
            this._steveIo
        );
        let isUploadingReady = false;
        const ActivityMonitor = this._activityMonitor;
        const sessionExpiredTime =
            ActivityMonitor._options.pollingInterval * ActivityMonitor._options.maxInactivityPolls -
            1000;

        /** Checks current loading time and prevents user session times out */
        let startCheckTimer = function () {
            if (isUploadingReady === true) {
                // Loading is done, turn off the checker
                return;
            } else {
                // Extend user session, and load timer again
                ActivityMonitor._resetInactivityPolls();
                setTimeout(startCheckTimer, sessionExpiredTime);
            }
        };
        const promList = [];
        promList.push(this._configAuditPolicy.loadConceptItemLabelValues(this._activeOrgId));
        promList.push(
            this._configAuditPolicy.loadRsiLabelValues(this._activeOrgId, {
                "Config Modeling Only": {$ne: true},
            })
        );

        promList.push(this._commandSetEntityDef.loadCommandSets(this._activeOrgId));
        promList.push(this._tagsEntityDef.loadTags(this._activeOrgId));

        return Promise.all(promList)
            .then((values) => {
                const conceptItems = values[0];
                const rsiList = values[1];
                const cmdSets = values[2];
                const tags = values[3] || [];
                this._tagNameToIdMap = tags.reduce((acc, tag) => {
                    acc[tag.name] = tag.id;
                    return acc;
                }, {});
                this._conceptItemLabelValues = conceptItems.reduce(
                    (conceptItemLabelValueAccumulator, conceptItem) => {
                        conceptItemLabelValueAccumulator[conceptItem.label] = conceptItem;
                        return conceptItemLabelValueAccumulator;
                    },
                    {}
                );
                this._rsiIdsMap = rsiList.reduce((acc, item) => {
                    acc[item.label] = item.value;
                    return acc;
                }, {});
                this._cmdSetValLabelMap = {
                    All: "all",
                    "All Configuration": "allConfig",
                    "All Operational": "allOperational",
                };
                cmdSets.forEach((cmdSet) => {
                    this._cmdSetValLabelMap[cmdSet.name] = cmdSet.id;
                });
                return this._modalUserFeedback.displayCustom(importScreenComponent, {
                    dontHide: true,
                });
            })
            .then(async (result) => {
                this._modalUserFeedback.displayLoading({
                    message: "Importing your file... This could take a moment",
                });
                // Let check results in 15 minutes
                setTimeout(startCheckTimer, sessionExpiredTime);

                switch (result.action) {
                    case "bulkImport":
                        let importedData = result.importData;
                        let importedPolicies = [];
                        for (let i = 0; i < importedData.length; i++) {
                            let importedDataObject = importedData[i];
                            // let ruleErrors = [];
                            let rulesFailed = false;
                            let importedPolicy = new this._configAuditPolicy(this._activeOrgId),
                                // validateErrors = [],
                                // validateErrorString = "",
                                importPolicyName =
                                    importedDataObject.name && importedDataObject.name.trim();
                            // importedPolicyNames.push(importPolicyName || "N/A");
                            if (importPolicyName && importPolicyName.length > 0) {
                                importedPolicy.setName(importPolicyName);
                            } else {
                                const error = {
                                    errorMsg: "imported policy must have a name",
                                    errorCode: "SyntaxError",
                                };
                                importedPolicies.push(
                                    Promise.reject({
                                        name: importPolicyName,
                                        err: error,
                                    })
                                );
                                return;
                            }
                            const permLevels = [
                                "auditManageLevel1",
                                "auditManageLevel2",
                                "auditManageLevel3",
                            ];
                            let importPolicyPermissionLevel = _.get(
                                importedDataObject,
                                "permissionLevel"
                            );
                            if (
                                importPolicyPermissionLevel &&
                                permLevels.indexOf(importPolicyPermissionLevel) >= 0
                            ) {
                                importedPolicy.setPermissionLevel(importPolicyPermissionLevel);
                            } else {
                                let error;
                                if (
                                    Object.keys(importedDataObject).indexOf("permissionLevel") >= 0
                                ) {
                                    error = {
                                        errorMsg:
                                            "imported policy must have a valid permission level",
                                        errorCode: "SyntaxError",
                                    };
                                } else {
                                    error = {
                                        errorMsg:
                                            "Sorry! The audit policy you are trying to import is in a format that is no longer supported.",
                                        errorCode: "FormatError",
                                    };
                                }

                                importedPolicies.push(
                                    Promise.reject({
                                        name: importPolicyName,
                                        err: error,
                                    })
                                );
                                return;
                            }
                            if (importedDataObject.description) {
                                importedPolicy.setDescription(importedDataObject.description);
                            } else {
                                switch (importedDataObject.description) {
                                    case "":
                                        importedPolicy.setDescription("");
                                        break;
                                    case 0:
                                        importedPolicy.setDescription("0");
                                        break;
                                    case false:
                                        importedPolicy.setDescription("false");
                                        break;
                                    default:
                                        importedPolicy.setDescription("");
                                        break;
                                }
                            }
                            let conceptItemFailed = false;
                            let customFieldFailed = false;
                            let restSyncItemFailed = false;
                            let commandSetFailed = false;
                            let ruleError;
                            let formatFailed = false;
                            if (importedDataObject.rules) {
                                // let conceptItemId;
                                this.policyName = importedDataObject.name;
                                // console.log(importedDataObject.rules);
                                const templateString = JSON.stringify(importedDataObject.rules);
                                if (templateString.search(/customFieldValues\./gi) >= 0) {
                                    if (templateString.search(/customFieldValues\.<%/gi) <= 0) {
                                        formatFailed = true;
                                    }
                                }
                                if (!formatFailed) {
                                    let rules;
                                    try {
                                        rules =
                                            await this._CustomFields.resolveTemplatedCustomFields(
                                                this._activeOrgId,
                                                importedDataObject.rules
                                            );
                                        importedDataObject.rules = rules;
                                        const ruleString = JSON.stringify(rules);
                                        if (ruleString.search(/customFieldValues\.("|')/gi) >= 0) {
                                            customFieldFailed = true;
                                        }
                                        if (
                                            !customFieldFailed &&
                                            ruleString.search(/\$((eq)|(ne))":""/gi) >= 0
                                        ) {
                                            customFieldFailed = true;
                                        }
                                        // console.log(customFieldFailed);
                                        importedDataObject.rules.forEach((rule, idx) => {
                                            conceptItemFailed = this.processRule(
                                                importedDataObject,
                                                rule,
                                                idx,
                                                "conceptItem"
                                            );

                                            restSyncItemFailed = this.processRule(
                                                importedDataObject,
                                                rule,
                                                idx,
                                                "rsiId"
                                            );
                                            commandSetFailed = this.processRule(
                                                importedDataObject,
                                                rule,
                                                idx,
                                                "cmdSet"
                                            );
                                            if (!commandSetFailed) {
                                                restSyncItemFailed =
                                                    restSyncItemFailed ||
                                                    this.processRule(
                                                        importedDataObject,
                                                        rule,
                                                        idx,
                                                        "cmdResultRsiId"
                                                    );
                                                conceptItemFailed =
                                                    conceptItemFailed ||
                                                    this.processRule(
                                                        importedDataObject,
                                                        rule,
                                                        idx,
                                                        "cmdResultConceptItem"
                                                    );
                                            }
                                            if (rule.ifExpression) {
                                                importedDataObject.rules[idx].ifExpression =
                                                    convertExpressionTags(
                                                        rule.ifExpression,
                                                        this._tagNameToIdMap
                                                    );
                                            }
                                        });

                                        if (!conceptItemFailed && !customFieldFailed) {
                                            importedPolicy.setRules(importedDataObject.rules);
                                        }
                                    } catch (err) {
                                        ruleError = err;
                                    }
                                }
                            }
                            if (!rulesFailed) {
                                // importedPolicies.push(importedPolicy.create());
                                if (ruleError) {
                                    importedPolicies.push(
                                        Promise.reject({
                                            name: importPolicyName,
                                            err: {
                                                errorMsg: ruleError
                                                    .toString()
                                                    .replace("Error: ", ""),
                                            },
                                        })
                                    );
                                } else if (formatFailed) {
                                    importedPolicies.push(
                                        Promise.reject({
                                            name: importPolicyName,
                                            err: {
                                                errorMsg: "Policy format is not supported",
                                            },
                                        })
                                    );
                                } else if (
                                    conceptItemFailed ||
                                    customFieldFailed ||
                                    restSyncItemFailed ||
                                    commandSetFailed
                                ) {
                                    let errorMsg = "One or more ";
                                    if (conceptItemFailed) {
                                        errorMsg += "concept items";
                                        if (
                                            customFieldFailed ||
                                            restSyncItemFailed ||
                                            commandSetFailed
                                        ) {
                                            errorMsg += ", ";
                                        }
                                    }
                                    if (customFieldFailed) {
                                        errorMsg += "custom fields";
                                        if (restSyncItemFailed || commandSetFailed) {
                                            errorMsg += ", ";
                                        }
                                    }
                                    if (restSyncItemFailed) {
                                        errorMsg += "RSIs";
                                        if (commandSetFailed) {
                                            errorMsg += ", ";
                                        }
                                    }
                                    if (commandSetFailed) {
                                        errorMsg += "command sets";
                                    }

                                    errorMsg += " failed";

                                    importedPolicies.push(
                                        Promise.reject({
                                            name: importPolicyName,
                                            err: {
                                                errorMsg,
                                            },
                                        })
                                    );
                                } else {
                                    importedPolicies.push(
                                        (() => {
                                            return importedPolicy
                                                .create()
                                                .then((data) => Promise.resolve(data))
                                                .catch((err) =>
                                                    Promise.reject({
                                                        name: importPolicyName,
                                                        err,
                                                    })
                                                );
                                        })()
                                    );
                                }
                            }
                        }

                        return Promise.allSettled(importedPolicies);
                }
            })
            .then((resultPolicies) => {
                // console.log(resultPolicies);
                isUploadingReady = true;
                const results = {
                    summary: {},
                    successMessages: [],
                    errorMessages: [],
                };
                results.summary = {
                    targetCount: resultPolicies.length,
                    successCount: 0,
                    failureCount: 0,
                    policyCreateCount: 0,
                };
                let successMessage = "";
                let errorMessage = "";
                resultPolicies.forEach((resultPolicy, idx) => {
                    if (resultPolicy.status === "fulfilled") {
                        results.summary.successCount++;
                        let isLastItem = resultPolicies.length + 1 === idx;
                        const msg =
                            "Policy - " + resultPolicy.value.name + ", imported successfully";
                        successMessage += msg + (isLastItem ? "" : "\r\n");
                        results.successMessages.push(msg);
                    } else {
                        results.summary.failureCount++;
                        errorMessage += `${resultPolicy.reason.err.errorMsg}\r\n`;
                        results.errorMessages.push(
                            `Policy: ${resultPolicy.reason.name} Error: ${resultPolicy.reason.err.errorMsg}\r\n`
                        );
                        let eMsg = this.generateErrorMsg(resultPolicy.reason.err) || "";
                        results.errorMessages = results.errorMessages.concat(eMsg);
                    }
                });
                results.successMessage = successMessage;
                results.errorMessage = errorMessage;
                results.summary.policyCreateCount = results.summary.successCount;
                successMessage += errorMessage;

                return this._modalUserFeedback.displayCustom(
                    new ImportSummaryComponent(results, this._logger)
                );
            })
            .catch((err) => {
                isUploadingReady = true;
                if (err.userRejected) {
                    this._logger.warn({error: err}, "ImportAction.run() - User Canceled.");
                    return this._modalUserFeedback.hideDisplayed();
                } else {
                    this._logger.warn({error: err}, "ImportAction.run() - Error");
                    return this._modalUserFeedback.displayError(err).catch(() => {
                        return Promise.resolve();
                    });
                }
            });
    }
}

module.exports = ImportAction;

ImportAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "$teve.entities.ConfigAuditPolicy",
    "$teve.userSession",
    "$teve.activityMonitor",
    "$teve.io",
    "gameEditor.activeView",
    "$teve.entities.CustomFields",
    "$teve.entities.CommandSet",
    "$teve.entities.Tags",
];

ImportAction.$displayDetails = {
    type: "Action",
    name: "ImportAction",
    icon: "configdrift.import_devices",
    shown: true,
    title: "Import policies",
    order: 3,
};
